import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { EntitlementsService } from '@treasury/domain/entitlements';
import { Feature } from '@treasury/domain/services/feature-flags';
import { TmContainer } from '@treasury/presentation';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

export const tagName = 'wires-container-sidebar';
@customElement(tagName)
export class WiresContainerSidebar extends TmContainer {
    @InjectProperty()
    declare private readonly navService: NavigationService;

    @InjectProperty()
    declare private readonly entitlementsService: EntitlementsService;

    @state()
    private domesticEntitlement = false;

    @state()
    private internationalEntitlement = false;

    @state()
    private domesticCreateCreditorEntitlement = false;

    @state()
    private internationalCreateCreditorEntitlement = false;

    @state()
    private hasWireFileUploadEntitlement = false;

    @state()
    private wiresFeatureFlagEnabled = false;

    private get hasWireEntitlement() {
        return this.domesticEntitlement || this.internationalEntitlement;
    }

    private get hasCreateCreditorEntitlement() {
        return (
            this.domesticCreateCreditorEntitlement || this.internationalCreateCreditorEntitlement
        );
    }

    protected render() {
        return html` <div class="px-2 py-2 bg-primary rounded-lg">
            <div class="flex justify-center mb-2 font-medium text-header">Create Wire</div>
            <div class="flex flex-col gap-2">
                ${this.hasWireEntitlement
                    ? html`<tm-action-button
                          .buttonTitle=${this.wiresFeatureFlagEnabled
                              ? 'Create new wire'
                              : 'Create wire from template'}
                          .buttonStyle=${'secondary'}
                          @click=${() => {
                              this.navService.navigate(
                                  this.wiresFeatureFlagEnabled
                                      ? `/wires/domestic/select/new/usd`
                                      : `/wires/templates/initiate`,
                                  {
                                      backToPaymentsHub: true,
                                  }
                              );
                          }}
                      ></tm-action-button>`
                    : nothing}
                ${this.hasCreateCreditorEntitlement
                    ? html`<tm-action-button
                          .buttonTitle=${`Create creditor`}
                          .buttonStyle=${'secondary'}
                          @click=${() => {
                              this.navService.navigate(`/wires/creditors/new/`);
                          }}
                      ></tm-action-button>`
                    : nothing}
                ${this.hasWireFileUploadEntitlement
                    ? html`<tm-action-button
                          .buttonTitle=${'Upload wire file'}
                          .buttonStyle=${'secondary'}
                          @click=${() => {
                              this.navService.navigate(`/wires/upload`);
                          }}
                      ></tm-action-button>`
                    : nothing}

                <tm-action-button
                    .buttonTitle=${'Create new wire file format'}
                    .buttonStyle=${'secondary'}
                    @click=${() => {
                        this.navService.navigate(`wires/files/new-format`);
                    }}
                ></tm-action-button>
            </div>
        </div>`;
    }

    protected async firstUpdated() {
        this.wiresFeatureFlagEnabled = await (
            await this.featureFlagService
        ).isEnabled(Feature.PwaCreateSingleWireManual);

        await this.tryFetch(
            async () => Promise.all([this.entitlementsService.getEntitlements()]),
            ([entitlements]) => {
                this.domesticEntitlement = entitlements.get('Wire, Domestic, Create Wire') || false;
                this.internationalEntitlement =
                    entitlements.get('Wire, International, Create Wire') || false;
                this.domesticCreateCreditorEntitlement =
                    entitlements.get('Wire, Domestic, Create Beneficiary') || false;
                this.internationalCreateCreditorEntitlement =
                    entitlements.get('Wire, International, Create Beneficiary') || false;
                this.hasWireFileUploadEntitlement =
                    entitlements.get('Upload Domestic Wire File') || false;
            }
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WiresContainerSidebar;
    }
}
